import React, { useEffect, useRef, useState } from 'react';

export interface SearchableSelectOption {
  value: string;
  label: string;
}

interface SearchableSelectProps {
  options: SearchableSelectOption[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  noMatchesLabel?: string;
}

// Plain `<select>` elements don't scale to a category list with hundreds of
// entries (10K+ clipart SVGs can spread across that many distinct categories).
// Lightweight text-filterable dropdown — no server round-trip, filters the
// already-fetched `options` list client-side.
export function SearchableSelect({ options, value, onChange, placeholder, searchPlaceholder, noMatchesLabel }: SearchableSelectProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onClickOutside(e: MouseEvent) {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  const selectedLabel = options.find(o => o.value === value)?.label ?? '';
  const filtered = query.trim()
    ? options.filter(o => o.label.toLowerCase().includes(query.trim().toLowerCase()))
    : options;

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        style={{
          width: '100%', padding: 8, border: '1px solid var(--border)', borderRadius: 4,
          fontSize: 14, background: 'white', textAlign: 'left', cursor: 'pointer',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 6,
        }}
      >
        <span style={{ color: selectedLabel ? 'inherit' : '#94a3b8', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {selectedLabel || placeholder}
        </span>
        <span style={{ color: '#94a3b8', flexShrink: 0 }}>▾</span>
      </button>

      {open && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 20, marginTop: 4,
          background: 'white', border: '1px solid var(--border)', borderRadius: 4,
          boxShadow: '0 4px 12px rgba(0,0,0,0.12)', maxHeight: 260, overflowY: 'auto',
        }}>
          <input
            autoFocus
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder={searchPlaceholder}
            style={{ width: '100%', padding: 8, border: 'none', borderBottom: '1px solid var(--border)', fontSize: 14, boxSizing: 'border-box', outline: 'none' }}
          />
          {filtered.length === 0 && (
            <div style={{ padding: 10, fontSize: 13, color: '#94a3b8' }}>{noMatchesLabel}</div>
          )}
          {filtered.map(o => (
            <div
              key={o.value}
              onClick={() => { onChange(o.value); setOpen(false); setQuery(''); }}
              style={{
                padding: '8px 10px', fontSize: 14, cursor: 'pointer',
                background: o.value === value ? 'var(--selection-tint)' : 'transparent',
              }}
            >
              {o.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
